import { useRecordContext } from "ra-core";

import type { Contact, ContactTouch } from "../types";

/**
 * Attribution journey - the contact's marketing touches as a visual timeline,
 * HubSpot-style: first touch and last touch called out, everything between
 * shown in order with channel identity. Demo data rides on the contact
 * record; production renders touch.captured events from the spine.
 */

const CHANNEL_STYLE: Record<
  ContactTouch["channel"],
  { label: string; chip: string }
> = {
  organic: { label: "Organic search", chip: "bg-[#8fb586]" },
  paid: { label: "Paid", chip: "bg-[#c97a63]" },
  social: { label: "Social", chip: "bg-[#9db4cc]" },
  email: { label: "Email", chip: "bg-[#b07d3f]" },
  direct: { label: "Direct", chip: "bg-[#94a3b8]" },
  referral: { label: "Referral", chip: "bg-[#d9b36b]" },
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const ContactJourney = () => {
  const record = useRecordContext<Contact>();
  const touches = [...(record?.touches ?? [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  if (touches.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm font-medium text-muted-foreground mb-1">
        Journey
        <span className="ml-1.5 text-[10px] font-normal tabular-nums rounded-full bg-muted px-1.5 py-0.5">
          {touches.length}
        </span>
      </h3>
      {/* Capped at ~5 touches; longer journeys scroll inside the card */}
      <div className="relative pl-4 max-h-72 overflow-y-auto">
        {/* spine */}
        <div className="absolute left-[5px] top-2 bottom-2 w-px bg-border" />
        <div className="flex flex-col gap-3">
          {touches.map((t, i) => {
            const isFirst = i === 0;
            const isLast = i === touches.length - 1 && touches.length > 1;
            const style = CHANNEL_STYLE[t.channel];
            return (
              <div key={`${t.date}-${i}`} className="relative">
                <span
                  className={`absolute -left-4 top-1 w-[11px] h-[11px] rounded-full border-2 border-background ${style.chip} ${
                    isFirst || isLast ? "ring-1 ring-foreground/40" : ""
                  }`}
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded text-white ${style.chip}`}
                  >
                    {style.label}
                  </span>
                  {isFirst && (
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-foreground/70">
                      First touch
                    </span>
                  )}
                  {isLast && (
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-foreground/70">
                      Last touch
                    </span>
                  )}
                </div>
                <p className="text-sm leading-tight mt-0.5">
                  {t.source}
                  {t.medium ? ` · ${t.medium}` : ""}
                  {t.campaign ? ` · ${t.campaign}` : ""}
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {fmtDate(t.date)}
                  {t.landingPage ? ` · ${t.landingPage}` : ""}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
